// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import moment, { Moment } from 'moment';
import { combineLatest, map } from 'rxjs';
import { LocalDateFormat } from '../../../../_interfaces/data/local-data-format-model';
import { Settings } from '../../../../_interfaces/settings-model';
import localDateFormatsJson from '../../../../config/localDateFormats.json';
import { APICallGateComponent } from '../../../shared/components/api-call-gate.component';
import { SettingsService } from '../../../shared/services/settings.service';
import { RouteService } from '../../services/routeService.service';
import { SystemOverviewService } from '../../services/systemOverview.service';

@Component({
  selector: 'app-component-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    NgIf,
    NgTemplateOutlet,
    NgFor,
    APICallGateComponent,
    AsyncPipe,
    TranslateModule,
  ],
})
export class SettingsComponent implements OnInit {
  @Input({ required: true }) menuMode = false;
  @Input({ required: false }) modalitySelectorMode = false;
  @ViewChild('drawer') drawer!: ElementRef<HTMLElement>;

  settings: Settings;

  modalities = this.systemOverviewService.modalities;
  modalityIds$ = this.settingsController.modalityIds$;

  set drawerVisible(v: boolean) {
    if (v) {
      this.drawer.nativeElement.setAttribute('visible', '');
    } else {
      this.drawer.nativeElement.removeAttribute('visible');
    }
  }

  get drawerVisible(): boolean {
    return this.drawer.nativeElement.hasAttribute('visible');
  }

  modalitiesWithactiveState$ = combineLatest({
    modalities: this.modalities.lastSuccessfulData$,
    modalityIds: this.modalityIds$,
  }).pipe(
    map(({ modalities, modalityIds }) => {
      return modalities.map(({ displayName: label, id }) => ({
        label,
        id,
        active: modalityIds.indexOf(id) !== -1 || null
      }));
    })
  );

  public localDateFormats: LocalDateFormat[];

  constructor(
    protected settingsController: SettingsService,
    protected systemOverviewService: SystemOverviewService,
    protected routeService: RouteService
  ) {
    this.settings = this.settingsController.getSettings();
    this.settingsController.settings$
      .pipe(takeUntilDestroyed())
      .subscribe((settings) => {
        this.settings = settings;
      });
    this.localDateFormats = localDateFormatsJson;
  }

  ngOnInit(): void {
    this.systemOverviewService.modalities.request();
  }

  onDateFormatChanged(event: Event): void {
    const target = (event as unknown as { currentTarget: HTMLElement })
      .currentTarget;
    const active = target.hasAttribute('active');
    if (!active) {
      return;
    }
    const localDateFormatId = parseInt(target.getAttribute('value') || '');

    if (localDateFormatId === this.settings.localDateFormatId) {
      return;
    }

    this.settings.localDateFormatId = localDateFormatId;
    this.settingsController.updateSettings(this.settings);
  }

  get sampleDate(): string {
    const sampleDates = [
      moment().startOf('day').add(0, 'day'),
      moment().startOf('day').add(1, 'day'),
      moment().startOf('day').add(2, 'day'),
      moment().startOf('day').add(3, 'day'),
    ];
    const asc = (a: Moment, b: Moment) => a.diff(b);
    const desc = (a: Moment, b: Moment) => b.diff(a);
    return (
      this.settings.isTimelineInAscendingOrder
        ? [...sampleDates].sort(asc)
        : [...sampleDates].sort(desc)
    )
      .map((a) => a.format('ddd'))
      .join(' - ');
  }

  onToggleInvertTimeline(event: Event): void {
    const isToggleActive =
      (event.target as unknown as HTMLElement)?.getAttribute('active') !== null;
    if (isToggleActive === this.settings.isTimelineInAscendingOrder) {
      return;
    }

    this.settings.isTimelineInAscendingOrder = isToggleActive;
    this.settingsController.updateSettings(this.settings);
  }
}
